// Enums
export enum UserRole {
  ADMIN = 'ADMIN',
  PARCEIRO = 'PARCEIRO',
  PROPRIETARIO = 'PROPRIETARIO',
  USUARIO = 'USUARIO',
  MOTORISTA = 'MOTORISTA'
}

export enum FuelType {
  GASOLINA_COMUM = 'GASOLINA_COMUM',
  GASOLINA_ADITIVADA = 'GASOLINA_ADITIVADA',
  ETANOL = 'ETANOL',
  DIESEL = 'DIESEL',
  DIESEL_S10 = 'DIESEL_S10'
}

export enum CashbackStatus {
  PENDENTE = 'PENDENTE',
  AGUARDANDO_CODIGO = 'AGUARDANDO_CODIGO',
  CODIGO_GERADO = 'CODIGO_GERADO',
  CODIGO_VALIDADO = 'CODIGO_VALIDADO',
  CODIGO_EXPIRADO = 'CODIGO_EXPIRADO'
}

export enum InviteStatus {
  PENDENTE = 'PENDENTE',
  ACEITO = 'ACEITO',
  EXPIRADO = 'EXPIRADO',
  CANCELADO = 'CANCELADO'
}

// Interfaces
export interface User {
  id: string;
  email: string;
  fullName: string;
  cpfCnpj?: string;
  telefone?: string;
  nivelAcesso: UserRole;
  pontosTotais: number;
  proprietarioId?: string;
  veiculosAutorizados?: string[];
  statusAtivo: boolean;
  // Cartão do motorista
  temFoto?: boolean;
  aparelhoVinculado?: boolean;
  aparelhoVinculadoEm?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Vehicle {
  id: string;
  usuario: User;
  placa: string;
  modelo?: string;
  marca?: string;
  ano?: string;
  cor?: string;
  tipoCombustivel?: FuelType;
  quilometragemAtual: number;
  createdAt: string;
  updatedAt: string;
}

// Horário de funcionamento em um dia da semana (1 = segunda ... 7 = domingo).
// fecha antes de abre: fecha no dia seguinte (ex.: 18:00 às 02:00)
export interface OpeningHour {
  diaSemana: number;
  abre: string; // HH:mm
  fecha: string; // HH:mm
}

export interface FuelStation {
  id: string;
  parceiro?: User;
  parceiroId?: string;
  parceiroNome?: string;
  nomePosto: string;
  cnpj?: string;
  // Endereço estruturado
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  endereco?: string; // Mantido para compatibilidade
  enderecoCompleto?: string;
  telefone?: string;
  email?: string;
  horarioFuncionamento?: string; // Texto livre legado
  aberto24h?: boolean;
  horarios?: OpeningHour[];
  latitude?: number;
  longitude?: number;
  // Campos legados: a configuração de benefícios agora é por combustível.
  percentualCashback?: number;
  percentualLucro?: number;
  litrosMinimosDesconto?: number;
  descontoPorLitro?: number;
  exigeEnchimentoCompleto?: boolean;
  bandeira?: string;
  fotoUrl?: string | null;
  ativo?: boolean;
  // Pedido de responsabilidade aguardando aprovação (resposta do cadastro feito por dono de posto)
  reivindicacaoStatus?: ClaimStatus;
  // Lista de combustíveis do posto
  combustiveis?: Fuel[];
  createdAt: string;
  updatedAt: string;
}

export interface Fuel {
  id: string;
  posto?: FuelStation;
  postoId?: string;
  tipoCombustivel: FuelType;
  precoAtual: number;
  descontoReais: number;
  percentualCashback?: number;
  percentualLucro?: number;
  litrosMinimosDesconto?: number;
  descontoPorLitro?: number;
  precoAtualizadoEm?: string;
  precoOrigem?: 'PARCEIRO' | 'ANP' | 'ADMIN' | 'COLABORATIVO';
  createdAt: string;
  updatedAt: string;
}

// Request para criar/atualizar combustível
export interface FuelRequest {
  tipoCombustivel: FuelType;
  precoAtual: number;
  descontoReais?: number;
  percentualCashback?: number;
  percentualLucro?: number;
  litrosMinimosDesconto?: number;
  descontoPorLitro?: number;
}

export interface Refuel {
  id: string;
  usuario?: User;
  veiculo?: Vehicle;
  combustivel?: Fuel;
  posto?: FuelStation;
  // Campos retornados pela API (RefuelResponse)
  usuarioId?: string;
  usuarioNome?: string;
  veiculoId?: string;
  veiculoPlaca?: string;
  combustivelId?: string;
  tipoCombustivel?: FuelType;
  postoId?: string;
  postoNome?: string;
  dataAbastecimento: string;
  litros: number;
  precoLitro: number;
  descontoLitro: number;
  valorTotal: number;
  quilometragem?: number;
  mediaConsumo?: number;
  pontosGerados: number;
  valorEconomizado: number;
  valorCashback?: number;
  codigoCashback?: string;
  statusCodigo: CashbackStatus;
  dataGeracaoCodigo?: string;
  dataValidacaoCodigo?: string;
  pontosLiberados: boolean;
  mediaConsumoLiberada: boolean;
  valorEconomizadoLiberado: boolean;
  createdAt: string;
  updatedAt: string;
}

// Driver Invite
export interface DriverInvite {
  id: string;
  nome: string;
  email: string;
  token: string;
  proprietarioId: string;
  proprietarioNome?: string;
  status: InviteStatus;
  dataExpiracao: string;
  createdAt: string;
  updatedAt?: string;
}

// Relatórios do Parceiro
export interface StationSummary {
  postoId: string;
  nomePosto: string;
  totalAbastecimentos: number;
  totalLitros?: number;
  totalValor?: number;
  totalDesconto?: number;
  totalCashback?: number;
  cashbackGerado?: number;
  totalCashbackGerado?: number;
  codigosPendentes: number;
  codigosValidados: number;
}

export interface ParceiroReportResponse {
  totalPostos: number;
  totalAbastecimentos: number;
  totalLitrosAbastecidos?: number;
  totalValorMovimentado?: number;
  totalDescontos?: number;
  codigosPendentes: number;
  codigosValidados: number;
  resumoPorPosto: StationSummary[];
}

// API Response
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface AuthResponse {
  token: string;
  type: string;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

// Complete Driver Registration
export interface CompleteDriverRegistrationRequest {
  token: string;
  telefone?: string;
  cpfCnpj?: string;
  password: string;
}

export interface ValidateInviteResponse {
  id: string;
  nome: string;
  email: string;
  token: string;
  inviteLink: string;
  status: string;
  dataExpiracao: string;
  createdAt: string;
  proprietarioId: string;
  proprietarioNome: string;
  mensagem?: string;
}

// Station Request (para criação/edição)
export interface StationRequest {
  nomePosto: string;
  cnpj?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  telefone?: string;
  email?: string;
  horarioFuncionamento?: string; // Texto livre legado
  aberto24h?: boolean;
  horarios?: OpeningHour[];
  latitude?: number;
  longitude?: number;
  percentualCashback?: number;
  percentualLucro?: number;
  litrosMinimosDesconto?: number;
  descontoPorLitro?: number;
  exigeEnchimentoCompleto?: boolean;
  proprietarioId?: string; // ID do parceiro dono do posto (para admin)
}

// Combustíveis com preço publicado pelo posto (inclui GNV, ausente do enum legado)
export const STATION_FUEL_TYPES = [
  'GASOLINA_COMUM',
  'GASOLINA_ADITIVADA',
  'ETANOL',
  'DIESEL',
  'DIESEL_S10',
  'GNV',
] as const;

export type StationFuelType = typeof STATION_FUEL_TYPES[number];

export interface StationPricesRequest {
  precos: { tipoCombustivel: StationFuelType; preco: number }[];
}

// Pedido do dono de posto para assumir um posto (aprovado pelo admin)
export type ClaimStatus = 'PENDENTE' | 'APROVADO' | 'REJEITADO';

export interface StationClaim {
  id: string;
  status: ClaimStatus;
  novoPosto: boolean;
  postoId: string;
  postoNome: string;
  postoCnpj?: string;
  postoCidade?: string;
  postoEstado?: string;
  usuarioId?: string;
  usuarioNome?: string;
  usuarioEmail?: string;
  usuarioCpfCnpj?: string;
  documentoConfere?: boolean;
  motivoRejeicao?: string;
  createdAt: string;
  decididoEm?: string;
}

// Request para criar abastecimento pelo posto
export interface StationRefuelRequest {
  placa: string;
  combustivelId: string;
  litros: number;
  quilometragem: number;
}

// Response do abastecimento criado pelo posto
export interface StationRefuelResponse {
  refuelId: string;
  codigoCashback: string;
  statusCodigo: CashbackStatus;
  dataGeracaoCodigo: string;
  veiculoId: string;
  placa: string;
  modeloVeiculo: string;
  usuarioNome: string;
  combustivelNome: string;
  litros: number;
  precoLitro: number;
  descontoLitro: number;
  valorTotal: number;
  quilometragem: number;
  dataAbastecimento: string;
  pontosGerados: number;
  valorEconomizado: number;
  valorCashback: number;
  mensagem: string;
}

// Importações de dados abertos da ANP
export type ImportType = 'ANP_CADASTRO' | 'ANP_PRECOS' | 'GEOCODIFICACAO' | 'CONTATO_RECEITA';
export type ImportStatus = 'EM_ANDAMENTO' | 'SUCESSO' | 'ERRO';

export interface DataImportRun {
  id: string;
  tipo: ImportType;
  fonte?: string;
  status: ImportStatus;
  iniciadoEm: string;
  finalizadoEm?: string;
  linhasLidas: number;
  linhasIgnoradas: number;
  postosCriados: number;
  postosAtualizados: number;
  precosAtualizados: number;
  mensagem?: string;
}

// Oportunidades: postos do cadastro da ANP que ainda não estão no Abastece+
export interface OpportunityPrice {
  tipoCombustivel: FuelType;
  preco: number;
  coletadoEm?: string;
}

export interface Opportunity {
  id: string;
  nomePosto: string;
  cnpj?: string;
  bandeira?: string;
  endereco?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cep?: string;
  cidade?: string;
  estado?: string;
  // O cadastro da ANP não traz telefone nem e-mail; vêm da Receita (pelo CNPJ) ou de cadastro manual
  telefone?: string;
  email?: string;
  // Quando o contato foi buscado na Receita (vazio = nunca consultado)
  contatoConsultadoEm?: string;
  anpAutorizacao?: string;
  anpCodigoIsimp?: string;
  latitude?: number;
  longitude?: number;
  solicitacaoPendente: boolean;
  precos: OpportunityPrice[];
}

export interface OpportunityPage {
  items: Opportunity[];
  total: number;
  page: number;
  size: number;
  estados: string[];
}

export interface OpportunityFilters {
  estado?: string;
  cidade?: string;
  busca?: string;
  apenasComPreco?: boolean;
  contato?: OpportunityContactFilter;
  page?: number;
  size?: number;
}

export type OpportunityContactFilter = 'COM_TELEFONE' | 'COM_EMAIL' | 'COM_CONTATO' | 'SEM_CONTATO';

// Resultado da busca de contato de uma oportunidade pelo CNPJ na Receita
export interface OpportunityContactLookup {
  id: string;
  telefone?: string;
  email?: string;
  contatoConsultadoEm?: string;
  atualizado: boolean;
}
